import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TDynamicFormBuilderProps } from '@/console/features/dynamic-form/dynamic-form.types';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { FormSelect } from '@/shared/components/ui/form/components/form-select/form-select';
import { FormToggle } from '@/shared/components/ui/form/components/form-toggle/form-toggle';
import { RadioGroup } from '@/shared/components/ui/form/components/radio-group/radio-group';
import {
  FeatureConfigOptionType,
  InputTypeEnum,
} from '@/shared/types/types.generated';

export const DynamicForm: React.FC<TDynamicFormBuilderProps> = ({
  options,
  headline,
  formNamePrefix,
}) => {
  const { t } = useTranslation();

  const getLabelTranslation = (label: string) => {
    return t(`dynamic_form:${label}`);
  };

  const getInputName = (value: string) => {
    if (formNamePrefix) {
      return `${formNamePrefix}.${value}`;
    }

    return value;
  };

  const renderOption = (option: FeatureConfigOptionType) => {
    if (option.inputType === InputTypeEnum.InputNumber) {
      return (
        <FormInput
          label={getLabelTranslation(option.label)}
          key={option.value}
          name={getInputName(option.value)}
          required={option.required}
          defaultValue={option.defaultValue}
          hint={getLabelTranslation(option.description)}
          type="number"
        />
      );
    }

    if (option.inputType === InputTypeEnum.Switch) {
      const leftOption = option.inputOptions?.[0];
      const rightOption = option.inputOptions?.[1];

      return (
        <FormToggle
          key={option.value}
          name={getInputName(option.value)}
          rightLabel={
            rightOption ? getLabelTranslation(rightOption.label) : undefined
          }
          leftLabel={
            leftOption ? getLabelTranslation(leftOption.label) : undefined
          }
          label={getLabelTranslation(option.label)}
          defaultValue={option.defaultValue}
          required={option.required}
          hint={getLabelTranslation(option.description)}
        />
      );
    }

    if (option.inputType === InputTypeEnum.RadioButton) {
      return (
        <RadioGroup
          key={option.value}
          name={getInputName(option.value)}
          label={getLabelTranslation(option.label)}
          options={(option.inputOptions ?? []).map((option) => {
            return {
              label: getLabelTranslation(option.label),
              value: option.value,
            };
          })}
          required={option.required}
          optionDirection="column"
          defaultValue={option.defaultValue}
          hint={getLabelTranslation(option.description)}
        />
      );
    }

    if (option.inputType === InputTypeEnum.Select) {
      return (
        <FormSelect
          key={option.value}
          name={getInputName(option.value)}
          label={getLabelTranslation(option.label)}
          options={option.inputOptions ?? []}
          required={option.required}
          defaultValue={option.defaultValue}
          hint={getLabelTranslation(option.description)}
        />
      );
    }

    if (option.inputType === InputTypeEnum.InputText) {
      return (
        <FormInput
          label={getLabelTranslation(option.label)}
          key={option.value}
          name={getInputName(option.value)}
          required={option.required}
          defaultValue={option.defaultValue}
          hint={getLabelTranslation(option.description)}
        />
      );
    }
  };

  return (
    <Stack gap="1rem">
      {headline && <Typography variant="p6">{headline}</Typography>}
      {options.map(renderOption)}
    </Stack>
  );
};
